// «Copiar tabla» (ios/Sources/Views/MarkdownTableView.swift, PARITY P313): a hover button on the
// table card and an item at the top of the message's right-click menu when the click landed on a
// table. Writes BOTH flavours (core/markdownTableCopy.ts): HTML for documents and mail, TSV for
// spreadsheets.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MarkdownTableCopy } from '../../../core/markdownTableCopy';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import './extras.css';

export interface TableRef {
  header: string[];
  rows: string[][];
}

/** Each drawn table card → its cells, so a right-click anywhere inside finds what to copy. */
const cards = new WeakMap<Element, TableRef>();

/** The table the event landed on, or null. */
export function tableUnder(target: EventTarget | null): TableRef | null {
  const el = (target as Element | null)?.closest?.('.table-copy-wrap');
  return el ? cards.get(el) ?? null : null;
}

/** Copies the table; resolves true when the clipboard took it. */
export async function copyTable(table: TableRef): Promise<boolean> {
  const tsv = MarkdownTableCopy.tsv(table.header, table.rows);
  const html = MarkdownTableCopy.html(table.header, table.rows);
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([tsv], { type: 'text/plain' }) }),
      ]);
      return true;
    }
    await navigator.clipboard.writeText(tsv);
    return true;
  } catch {
    try {
      await navigator.clipboard.writeText(tsv);
      return true;
    } catch {
      return false;
    }
  }
}

/** The table's card with its copy button (shown on hover / focus, ✓ for 1.4 s after a copy). */
export function TableCard({ header, rows, children }: TableRef & { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (ref.current) cards.set(ref.current, { header, rows });
  }, [header, rows]);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(id);
  }, [copied]);
  return (
    <div className="table-copy-wrap" ref={ref}>
      <div className="table-card">{children}</div>
      <button
        type="button"
        className={`table-copy${copied ? ' copied' : ''}`}
        title={t('channel.action.copyTable')}
        aria-label={t('channel.action.copyTable')}
        onClick={(e) => {
          e.stopPropagation();
          void copyTable({ header, rows }).then((ok) => ok && setCopied(true));
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={13} strokeWidth={2.2} />
      </button>
    </div>
  );
}
